import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { InteriorUtilities } from '../../types';

export default function Section6InteriorUtilities() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<InteriorUtilities>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.interior_utilities) {
      setData(state.formData.interior_utilities);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.interior_utilities, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Save data when it changes
    if (state.intake && data.intake_id) {
      saveSection('interior_utilities', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof InteriorUtilities, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Interior, Lot & Utilities</h2>
        
        <div className="space-y-6">
          {/* Basic Interior Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                # of Bedrooms
              </label>
              <input
                type="number"
                value={data.bedrooms || ''}
                onChange={(e) => updateField('bedrooms', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="0"
                min="0"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                # of Full Baths
              </label>
              <input
                type="number"
                value={(data as any).full_bathrooms || ''}
                onChange={(e) => updateField('full_bathrooms' as any, parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="0"
                min="0"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                # of Half Baths
              </label>
              <input
                type="number"
                value={(data as any).half_bathrooms || ''}
                onChange={(e) => updateField('half_bathrooms' as any, parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="0"
                min="0"
                max="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                # of Fireplaces
              </label>
              <input
                type="number"
                value={data.fireplaces || ''}
                onChange={(e) => updateField('fireplaces', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="0"
                min="0"
                max="10"
              />
            </div>
          </div>

          {/* Appliances */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appliances
            </label>
            <div className="space-y-2">
              {['Oven/Range', 'Subzero', 'Washer/Dryer'].map((item) => (
                <label key={item} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={((data as any).appliances_list || []).includes(item)}
                    onChange={(e) => {
                      const current: string[] = (data as any).appliances_list || [];
                      const updated = e.target.checked ? [...current, item] : current.filter((a: string) => a !== item);
                      updateField('appliances' as any, updated.join(', '));
                      updateField('appliances_list' as any, updated);
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).appliances_other || ''}
                  onChange={(e) => updateField('appliances_other' as any, e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                  placeholder="e.g., refrigerator, dishwasher"
                />
              </div>
            </div>
          </div>

          {/* Interior Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interior Features
            </label>
            <div className="space-y-2">
              {['Intercom', 'Auto Garage Door', 'Security System'].map((item) => (
                <label key={item} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={((data as any).interior_features_list || []).includes(item)}
                    onChange={(e) => {
                      const current: string[] = (data as any).interior_features_list || [];
                      const updated = e.target.checked ? [...current, item] : current.filter((f: string) => f !== item);
                      updateField('interior_features' as any, updated.join(', '));
                      updateField('interior_features_list' as any, updated);
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).interior_features_other || ''}
                  onChange={(e) => updateField('interior_features_other' as any, e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                  placeholder="e.g., crown molding, built-ins"
                />
              </div>
            </div>
          </div>

          {/* Pool */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pool
            </label>
            <div className="space-y-2">
              {['In Ground', 'Lap Pool', 'N/A'].map((item) => (
                <label key={item} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="pool_type"
                    value={item}
                    checked={(data as any).pool_type === item}
                    onChange={() => updateField('pool_type' as any, item)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="pool_type"
                  value="other"
                  checked={(data as any).pool_type === 'other'}
                  onChange={() => updateField('pool_type' as any, 'other')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).pool_other || ''}
                  onChange={(e) => updateField('pool_other' as any, e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          {/* Waterfront */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Waterfront
            </label>
            <div className="space-y-2">
              {['Direct', 'Beach Rights', 'Pond', 'N/A'].map((item) => (
                <label key={item} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="waterfront_type"
                    value={item}
                    checked={(data as any).waterfront_type === item}
                    onChange={() => updateField('waterfront_type' as any, item)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="waterfront_type"
                  value="other"
                  checked={(data as any).waterfront_type === 'other'}
                  onChange={() => updateField('waterfront_type' as any, 'other')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).waterfront_other || ''}
                  onChange={(e) => updateField('waterfront_other' as any, e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          {/* Lot Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lot Description
            </label>
            <div className="space-y-2">
              {['Nonconforming Lot', 'Water Views', 'Level Lot'].map((item) => (
                <label key={item} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={((data as any).lot_description_list || []).includes(item)}
                    onChange={(e) => {
                      const current: string[] = (data as any).lot_description_list || [];
                      const updated = e.target.checked ? [...current, item] : current.filter((l: string) => l !== item);
                      updateField('lot_description' as any, updated.join(', '));
                      updateField('lot_description_list' as any, updated);
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).lot_description_other || ''}
                  onChange={(e) => updateField('lot_description_other' as any, e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          {/* Utilities Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilities & Systems</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Heat Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heat Type
                </label>
                <select
                  value={data.heat_type || ''}
                  onChange={(e) => updateField('heat_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select heat type</option>
                  <option value="zoned">Zoned</option>
                  <option value="hot_water">Hot Water</option>
                  <option value="radiant">Radiant</option>
                  <option value="combination">Combination</option>
                  <option value="forced_air">Forced Air</option>
                  <option value="hydro_air">Hydro-Air</option>
                  <option value="steam">Steam</option>
                  <option value="electric">Electric</option>
                  <option value="other">Other</option>
                </select>
                {data.heat_type === 'other' && (
                  <input
                    type="text"
                    value={(data as any).heat_type_other || ''}
                    onChange={(e) => updateField('heat_type_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify heat type"
                  />
                )}
              </div>

              {/* Heat Fuel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heat Fuel
                </label>
                <select
                  value={data.heat_fuel || ''}
                  onChange={(e) => updateField('heat_fuel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select fuel type</option>
                  <option value="natural_gas">Natural Gas</option>
                  <option value="electric">Electric</option>
                  <option value="oil">Oil</option>
                  <option value="propane">Propane</option>
                  <option value="wood">Wood</option>
                  <option value="solar">Solar</option>
                  <option value="other">Other</option>
                </select>
                {data.heat_fuel === 'other' && (
                  <input
                    type="text"
                    value={(data as any).heat_fuel_other || ''}
                    onChange={(e) => updateField('heat_fuel_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify fuel type"
                  />
                )}
              </div>

              {/* Fuel Tank Location (for oil/propane) */}
              {(data.heat_fuel === 'oil' || data.heat_fuel === 'propane') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fuel Tank Location
                  </label>
                  <div className="space-y-2">
                    {['Above Ground', 'In Basement', 'In Garage', 'In Ground', 'N/A'].map((loc) => (
                      <label key={loc} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="fuel_tank_location"
                          value={loc}
                          checked={data.fuel_tank_location === loc}
                          onChange={() => updateField('fuel_tank_location', loc)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{loc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Water */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Water
                </label>
                <select
                  value={data.water || ''}
                  onChange={(e) => updateField('water', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select water source</option>
                  <option value="public_connected">Public Water Connected</option>
                  <option value="public_in_street">Public Water in Street</option>
                  <option value="private_well">Private Well</option>
                  <option value="other">Other</option>
                </select>
                {data.water === 'other' && (
                  <input
                    type="text"
                    value={(data as any).water_other || ''}
                    onChange={(e) => updateField('water_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify water source"
                  />
                )}
              </div>

              {/* Hot Water System */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hot Water System
                </label>
                <select
                  value={data.hot_water_system || ''}
                  onChange={(e) => updateField('hot_water_system', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select system</option>
                  <option value="electric">Electric</option>
                  <option value="natural_gas">Natural Gas</option>
                  <option value="other">Other</option>
                </select>
                {data.hot_water_system === 'other' && (
                  <input
                    type="text"
                    value={(data as any).hot_water_system_other || ''}
                    onChange={(e) => updateField('hot_water_system_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify hot water system"
                  />
                )}
              </div>

              {/* Cooling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cooling System
                </label>
                <select
                  value={data.cooling || ''}
                  onChange={(e) => updateField('cooling', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select cooling</option>
                  <option value="central_air">Central Air</option>
                  <option value="zoned">Zoned</option>
                  <option value="wall_unit">Wall Unit</option>
                  <option value="window_unit">Window Unit</option>
                  <option value="other">Other</option>
                </select>
                {data.cooling === 'other' && (
                  <input
                    type="text"
                    value={(data as any).cooling_other || ''}
                    onChange={(e) => updateField('cooling_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify cooling system"
                  />
                )}
              </div>

              {/* Sewage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sewage System
                </label>
                <select
                  value={data.sewage || ''}
                  onChange={(e) => updateField('sewage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">Select sewage system</option>
                  <option value="public_connected">Public Sewer Connected</option>
                  <option value="public_in_street">Public Sewer in Street</option>
                  <option value="septic">Septic</option>
                  <option value="other">Other</option>
                </select>
                {data.sewage === 'other' && (
                  <input
                    type="text"
                    value={(data as any).sewage_other || ''}
                    onChange={(e) => updateField('sewage_other' as any, e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Please specify sewage system"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            This information helps buyers understand the property's features and utility systems. 
            Don't worry if you're not sure about every detail - we can help you find this information.
          </p>
        </div>
      </div>
    </div>
  );
}
